/**
 * Backfill Task Dates Migration
 * 
 * This migration adds date fields to existing tasks that don't have them.
 * It runs once per sprint when loaded and sets sensible defaults:
 * 
 * - createdAt: Set to sprint's createdAt if available, otherwise now
 * - updatedAt: Set to now (since we're updating)
 * - startedAt: Set for tasks with status in "in_progress" category
 * - completedAt: Set for tasks with status in "completed" category
 * - dueDate: Left as-is (user-set)
 * - estimated duration: use estimatedDurationMinutes (see legacyEstimatedDuration migration)
 */

const MIGRATION_VERSION = 'task_dates_v1';

/**
 * Check if a sprint needs migration
 */
export const needsMigration = (sprint) => {
  if (!sprint || !sprint.tasks || sprint.tasks.length === 0) return false;
  
  // Check if any task is missing createdAt (our marker for unmigrated tasks)
  return sprint.tasks.some(task => !task.createdAt);
};

/**
 * Get status category from statuses array
 */
const getStatusCategory = (statusId, statuses) => {
  if (!statuses || !statusId) return null;
  const status = statuses.find(s => s.id === statusId);
  return status?.category || null;
};

/**
 * Backfill dates for a single task
 */
const backfillTaskDates = (task, sprintCreatedAt, statuses) => {
  // Skip if already has createdAt (already migrated)
  if (task.createdAt) return task;
  
  const now = new Date().toISOString();
  const statusCategory = getStatusCategory(task.status, statuses);
  
  // Determine appropriate dates based on task state
  const updates = {
    createdAt: sprintCreatedAt || now,
    updatedAt: now,
  };
  
  // If task is in progress, set startedAt
  if (statusCategory === 'in_progress' && !task.startedAt) {
    // Use a time slightly after creation
    updates.startedAt = sprintCreatedAt || now;
  }
  
  // If task is completed, set both startedAt and completedAt
  if (statusCategory === 'completed') {
    if (!task.startedAt) {
      updates.startedAt = sprintCreatedAt || now;
    }
    if (!task.completedAt) {
      updates.completedAt = now;
    }
  }
  
  return { ...task, ...updates };
};

/**
 * Migrate all tasks in a sprint
 * Returns the updated sprint (or original if no changes needed)
 */
export const migrateSprintTasks = (sprint, statuses) => {
  if (!needsMigration(sprint)) {
    return { sprint, migrated: false };
  }
  
  console.log(`📅 Migrating task dates for sprint: ${sprint.name || sprint.id}`);
  
  const sprintCreatedAt = sprint.createdAt || new Date().toISOString();
  
  const migratedTasks = sprint.tasks.map(task => 
    backfillTaskDates(task, sprintCreatedAt, statuses)
  );
  
  const migratedCount = migratedTasks.filter((t, i) => t !== sprint.tasks[i]).length;
  console.log(`✅ Migrated ${migratedCount} tasks`);
  
  return {
    sprint: {
      ...sprint,
      tasks: migratedTasks,
      _migrationVersion: MIGRATION_VERSION,
    },
    migrated: true,
    migratedCount,
  };
};

/**
 * Migrate multiple sprints (for bulk operations)
 */
export const migrateAllSprints = (sprints, getStatuses) => {
  let totalMigrated = 0;
  
  const migratedSprints = sprints.map(sprint => {
    const statuses = getStatuses ? getStatuses(sprint) : sprint.statuses;
    const { sprint: migratedSprint, migrated, migratedCount } = migrateSprintTasks(sprint, statuses);
    if (migrated) {
      totalMigrated += migratedCount;
    }
    return migratedSprint;
  });
  
  if (totalMigrated > 0) {
    console.log(`📅 Total tasks migrated across all sprints: ${totalMigrated}`);
  }
  
  return migratedSprints;
};

export default {
  needsMigration,
  migrateSprintTasks,
  migrateAllSprints,
  MIGRATION_VERSION,
};

